import {Stack, Typography} from '@mui/material';
import React, {FC} from 'react';

import {Change} from '../../utils/object-diff';
import {ArrayValue} from './array-value';
import {BooleanValue} from './boolean-value';
import {TextDiff} from './text-diff';
import {ValueChip} from './value-chip';

interface ChangeValueRendererProps {
  value: unknown;
  comparedValue?: unknown;
  isBeforeColumn?: boolean;
  fieldType?: Change['type'];
  yesLabel?: string;
  noLabel?: string;
}

/**
 * Composant principal pour afficher une valeur avec gestion des différents types
 */
export const ChangeValueRenderer: FC<ChangeValueRendererProps> = ({
  value,
  comparedValue,
  isBeforeColumn = false,
  fieldType,
  yesLabel = 'Oui',
  noLabel = 'Non',
}) => {
  // Valeur nulle ou undefined
  if (value === null || value === undefined) {
    return <ValueChip value="-" />;
  }

  // Valeur booléenne
  if (typeof value === 'boolean') {
    return <BooleanValue value={value} yesLabel={yesLabel} noLabel={noLabel} />;
  }

  // Tableau
  if (Array.isArray(value)) {
    return <ArrayValue values={value} comparedValues={Array.isArray(comparedValue) ? comparedValue : undefined} />;
  }

  // Objet
  if (typeof value === 'object') {
    const entries = Object.entries(value as Record<string, unknown>);
    if (entries.length === 0) {
      return <ValueChip value="-" />;
    }
    return (
      <Stack spacing={1}>
        {entries.map(([key, val]) => (
          <Stack key={key} spacing={0.5}>
            <Typography variant="caption" sx={{fontWeight: 500, color: '#666'}}>
              {key}:
            </Typography>
            <ChangeValueRenderer value={val} yesLabel={yesLabel} noLabel={noLabel} />
          </Stack>
        ))}
      </Stack>
    );
  }

  const stringValue = String(value);

  // Comparaison avec une autre valeur
  if (typeof comparedValue === 'string' && stringValue !== comparedValue) {
    // Pour les selects, afficher simplement avec une couleur
    if (fieldType === 'select') {
      return (
        <ValueChip
          value={stringValue}
          variant={isBeforeColumn ? 'warning' : 'success'}
          strikethrough={isBeforeColumn}
        />
      );
    }

    // Pour le texte, afficher les différences mot par mot
    return <TextDiff value={stringValue} comparedValue={comparedValue} isBeforeColumn={isBeforeColumn} />;
  }

  // Texte long (affichage typographique)
  if (stringValue.length > 120) {
    return (
      <Typography
        variant="body2"
        sx={{
          whiteSpace: 'pre-wrap',
          wordBreak: 'break-word',
          fontSize: '0.875rem',
          lineHeight: 1.4,
        }}
      >
        {stringValue}
      </Typography>
    );
  }

  // Valeur simple
  return <ValueChip value={stringValue} />;
};
